import { LatLng } from "leaflet";

import { BuildingType, UserRole, type Building, type PatchBuildingByIdRequestBody } from "@eisox/api/core-api";
import { ActionButton, Alert, Field, TextField } from "@eisox/design-system";
import { Map } from "@eisox/design-system/map";
import { FieldErrors, useAppForm, withForm } from "@eisox/form";
import { getDirtyValues } from "@eisox/tools";
import { z } from "@eisox/zod";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { buildingApi, meterApi, userApi } from "~/api";
import { DayRangePicker } from "~/components/day-range-picker";
import { DeleteDialog } from "~/components/delete-dialog";
import { SearchLocation } from "~/components/search-location";
import { canDeleteBuilding } from "~/features/buildings";
import { dayRangeSchema, toDayRangeIn, todayIn, toInstantRange } from "~/helpers/day-range.helper";
import { m } from "~/paraglide/messages";
import { getLocale } from "~/paraglide/runtime";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/buildings/general")({
  loader: async ({ context: { queryClient, role, permissions }, params: { buildingId } }) => {
    await Promise.all([
      role === UserRole.INSTALLER && queryClient.ensureQueryData(userApi.queries.building(buildingId)),
      permissions.building?.weatherStationId?.update &&
        queryClient.ensureQueryData(buildingApi.queries.weatherStations(buildingId)),
    ]);
  },
  component: RouteComponent,
});

const buildingGeneralSchema = z.object({
  name: z.string().nonempty(),
  type: z.enum(BuildingType),
  location: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
  weatherStationId: z.string().optional(),
  referencePeriod: dayRangeSchema.optional(),
});

type BuildingGeneralValues = z.input<typeof buildingGeneralSchema>;

const WeatherStationField = withForm({
  defaultValues: {} as BuildingGeneralValues,
  props: { buildingId: "" },
  render: function Render({ form, buildingId }) {
    const { data: weatherStations } = useSuspenseQuery({
      ...buildingApi.queries.weatherStations(buildingId),
      select: stations => [...stations].sort((a, b) => a.distanceKm - b.distanceKm),
    });
    const kilometerFormat = new Intl.NumberFormat(getLocale(), {
      style: "unit",
      unit: "kilometer",
      maximumFractionDigits: 1,
    });

    return (
      <form.AppField name="weatherStationId">
        {field => (
          <field.Select
            multiple={false}
            label={m.weather_station()}
            options={weatherStations.map(({ id, name, distanceKm }) => ({
              value: id,
              name: `${name} (${kilometerFormat.format(distanceKm)})`,
            }))}
            classNames={{ label: "text-primary-blue", control: "w-full max-w-[420px]" }}
          />
        )}
      </form.AppField>
    );
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { building, permissions, role } = useBuildingContext(Route.id);

  const { data: users } = useQuery({
    ...userApi.queries.building(building.id),
    enabled: role === UserRole.INSTALLER,
  });

  const showDeleteButton = canDeleteBuilding(role, users);
  const canEditStation = Boolean(permissions.building?.weatherStationId?.update);
  const canEditReference = Boolean(permissions.building?.referencePeriod?.update);
  const today = todayIn(building.timezone);
  const queryClient = useQueryClient();

  const { mutateAsync: update, isPending: isUpdatePending } = useMutation({
    mutationFn: (body: PatchBuildingByIdRequestBody) => buildingApi.update(building.id, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(buildingApi.queries.byId(building.id)),
        queryClient.invalidateQueries({ queryKey: meterApi.queries.consumption._def }),
      ]);
    },
  });

  const { mutate: remove, isPending: isDeletePending } = useMutation({
    meta: { toast: { action: "delete" } },
    mutationFn: () => buildingApi.remove(building.id),
    onSuccess: async () => {
      queryClient.removeQueries(buildingApi.queries.byId(building.id));
      queryClient.setQueryData(buildingApi.queries.all.queryKey, (oldData: Building[]) =>
        oldData.filter(h => h.id !== building.id),
      );
      await navigate({ to: "/buildings" });
    },
  });

  const defaultValues: BuildingGeneralValues = {
    name: building.name,
    type: building.type,
    location: {
      latitude: building.latitude,
      longitude: building.longitude,
    },
    weatherStationId: building.weatherStationId ?? undefined,
    referencePeriod: building.referencePeriod ? toDayRangeIn(building.referencePeriod, building.timezone) : undefined,
  };

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: buildingGeneralSchema },
    onSubmit: async ({ formApi, value }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      const { location, weatherStationId, referencePeriod, ...rest } = dirty;
      await update({
        ...rest,
        ...(location?.latitude !== undefined && { latitude: location.latitude }),
        ...(location?.longitude !== undefined && { longitude: location.longitude }),
        ...(weatherStationId && { weatherStationId }),
        ...(referencePeriod &&
          value.referencePeriod && { referencePeriod: toInstantRange(value.referencePeriod, building.timezone) }),
      });
      formApi.reset(value);
    },
  });

  return (
    <>
      <form
        className="gap-5 relative flex h-full flex-col overflow-x-hidden overflow-y-auto"
        data-testid="HouseGeneralSettings"
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.AppField name="name">
            {field => (
              <field.TextField
                classNames={{ label: "text-primary-blue", control: "w-full max-w-[420px]" }}
                label={m.name()}
                disabled={!permissions.building?.name?.update}
              />
            )}
          </form.AppField>
          <Field.Root>
            <Field.Label className="text-primary-blue">{m.type()}</Field.Label>
            <TextField.Root className="w-full max-w-[420px]" value={m[building.type]()} disabled />
          </Field.Root>
          {canEditStation && <WeatherStationField form={form} buildingId={building.id} />}
          {canEditReference && (
            <form.AppField name="referencePeriod">
              {field => (
                <Field.Root className="max-w-[420px]">
                  <Field.Label className="text-primary-blue">{m.building_reference_period()}</Field.Label>
                  <Alert severity="help">{m.building_reference_period_help()}</Alert>
                  <DayRangePicker value={field.state.value} onValueChange={field.handleChange} max={today} />
                  <FieldErrors />
                </Field.Root>
              )}
            </form.AppField>
          )}
          <form.AppField name="location">
            {field => (
              <>
                <Field.Root>
                  <Field.Label className="text-primary-blue">{m.position()}</Field.Label>
                  <SearchLocation
                    className={"max-w-150 w-full"}
                    onPositionChange={({ latitude, longitude }) => field.handleChange({ latitude, longitude })}
                    disabled={!permissions.building?.latitude?.update || !permissions.building.longitude?.update}
                  />
                </Field.Root>
                <Map.Root
                  ref={map => {
                    map?.on("click", e => field.handleChange({ latitude: e.latlng.lat, longitude: e.latlng.lng }));
                  }}
                  className="min-h-75 rounded-xl flex-1"
                >
                  <Map.Marker
                    draggable={permissions.building?.latitude?.update && permissions.building.longitude?.update}
                    position={new LatLng(field.state.value.latitude, field.state.value.longitude)}
                    eventHandlers={{
                      dragend: e =>
                        field.handleChange({
                          latitude: e.target.getLatLng().lat.toString(),
                          longitude: e.target.getLatLng().lng.toString(),
                        }),
                    }}
                  />
                </Map.Root>
              </>
            )}
          </form.AppField>
          <form.SubmitButton className="self-center" label={m.save()} />
          {showDeleteButton && (
            <DeleteDialog
              title={m.confirm_deletion({ resource: m.building() })}
              onDelete={() => remove()}
              description={
                <p className="text-center text-base whitespace-pre-line text-primary-dark-gray">
                  {m.building_deletion_warning()}
                </p>
              }
            >
              <ActionButton
                className="right-0 bottom-0 absolute"
                variant="danger"
                disabled={isUpdatePending || isDeletePending}
              >
                {m.delete()}
              </ActionButton>
            </DeleteDialog>
          )}
        </form.AppForm>
      </form>
    </>
  );
}
